import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import {
  getBarriosActivos, crearPedido,
} from '../lib/portalClienteApi'
import { ENVIO_GRATIS_DESDE } from '../lib/CartContext'
import { proximasFechasEntrega, calcularEnvio, cargarZonasDesdeDB } from '../lib/envios'
import { geocodificar, validarBarrioConDireccion, validarBarrioPorCoords } from '../lib/geo'
import { initMpDeviceId, getMpDeviceId } from '../lib/mpDeviceId'
import { getUTM } from '../lib/utm'
import MapPin from './MapPin'
import BloqueFacturacion from './BloqueFacturacion'
import TurnstileWidget from './TurnstileWidget'
import { VENTANAS_HORARIAS, labelCortoVentana, labelVentana } from '../lib/ventanasHorarias'

// TEMP · instrumentación diagnóstico bug P0 "checkout logueado rebota a /catalogo" (Nico).
// QUITAR tras diagnóstico. Todos los logs van prefijados [CHK] para filtrar en DevTools.
const DEBUG_CHECKOUT = true
function dbg(...a) { if (DEBUG_CHECKOUT) { try { console.log('%c[CHK]', 'color:#00A8B5;font-weight:700', ...a) } catch { /* noop */ } } }
function dbgWarn(...a) { if (DEBUG_CHECKOUT) { try { console.warn('[CHK]', ...a) } catch { /* noop */ } } }

// LoginInline — login SÍNCRONO (password + Turnstile) embebido en el paso Dirección del checkout,
// para visitantes que YA tienen cuenta. Usa signInWithPassword a propósito: el magic-link haría
// round-trip por email y perdería el paso del wizard. Al loguear OK, onSuccess() hidrata el form y
// useAuth pasa a logueado (sin recargar la página ni reiniciar el checkout). El carrito ya persiste.
function LoginInline({ onSuccess, onCancel }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!email.trim() || !password) { setError('Ingresá tu email y contraseña'); return }
    if (!turnstileToken) { setError('Completá la verificación anti-bot'); return }
    setLoading(true)
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(), password,
      options: { captchaToken: turnstileToken },
    })
    setLoading(false)
    if (err) {
      const m = err.message || ''
      if (/invalid login credentials/i.test(m)) setError('Email o contraseña incorrectos.')
      else if (/rate limit/i.test(m)) setError('Demasiados intentos seguidos. Esperá un minuto.')
      else if (/email not confirmed/i.test(m)) setError('Tenés que activar tu cuenta primero (revisá tu email).')
      else setError(m)
      return
    }
    onSuccess?.(data?.user)
  }

  return (
    <div className="cf-login-inline">
      <div className="cf-login-inline-head">
        <strong>Iniciá sesión</strong>
        <button type="button" className="cf-login-close" onClick={onCancel} aria-label="Cerrar">×</button>
      </div>
      <form onSubmit={handleSubmit} className="cf-login-form">
        <label className="cf-login-field">
          <span>Email</span>
          <input type="email" required autoComplete="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" />
        </label>
        <label className="cf-login-field">
          <span>Contraseña</span>
          <input type="password" required autoComplete="current-password" value={password}
            onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
        </label>
        <TurnstileWidget action="login-checkout" onToken={setTurnstileToken} />
        {error && <div className="cf-login-err">{error}</div>}
        <button type="submit" disabled={loading} className="cf-login-btn">
          {loading ? 'Ingresando…' : 'Ingresar y traer mis datos →'}
        </button>
      </form>
      <div className="cf-login-legal">
        ¿Olvidaste tu contraseña? Entrá desde <a href="/login">la página de ingreso</a>. Tu carrito queda guardado.
      </div>
      {/* El <style> se movió al render del CheckoutFlow: vivía acá adentro, y como este componente
          solo se monta al ABRIR el login, el botón "¿Ya sos cliente?" (que se muestra ANTES) quedaba
          sin CSS y se veía como un botón blanco default del navegador sobre el tema oscuro. */}
    </div>
  )
}

const LOGIN_INLINE_CSS = `
  /* Cartel clickeable (no un link suelto): se lee como acción y no se pierde en el formulario.
     appearance:none + colores explícitos → nunca hereda el botón blanco default del navegador. */
  .cf-login-link { -webkit-appearance:none; appearance:none; display:flex; align-items:center; gap:8px;
    width:100%; box-sizing:border-box; margin:0 0 16px; padding:11px 14px; cursor:pointer;
    background:rgba(0,168,181,0.08); border:1px solid rgba(0,168,181,0.30); border-radius:10px;
    color:#00A8B5; font-size:13px; font-weight:600; font-family:inherit; text-align:left;
    transition:background .15s, border-color .15s; }
  .cf-login-link::before { content:'👤'; font-size:15px; line-height:1; }
  .cf-login-link:hover { background:rgba(0,168,181,0.14); border-color:rgba(0,168,181,0.5); }
  .cf-login-link strong { text-decoration:underline; }
  .cf-login-inline { border:1px solid rgba(0,168,181,0.3); border-radius:12px; padding:16px; margin-bottom:16px;
    background:rgba(0,168,181,0.06); }
  .cf-login-inline-head { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
  .cf-login-inline-head strong { font-size:15px; }
  .cf-login-close { background:transparent; border:0; font-size:22px; line-height:1; cursor:pointer; color:inherit; opacity:.55; }
  .cf-login-close:hover { opacity:1; }
  .cf-login-form { display:flex; flex-direction:column; gap:10px; }
  .cf-login-field { display:flex; flex-direction:column; gap:4px; font-size:12px; font-weight:700; }
  .cf-login-field input { padding:10px 12px; border-radius:8px; border:1px solid rgba(128,128,128,0.35);
    background:rgba(255,255,255,0.04); color:inherit; font-size:14px; font-family:inherit; }
  .cf-login-err { color:#F87171; font-size:12.5px; font-weight:600; }
  .cf-login-btn { background:linear-gradient(135deg,#00A8B5,#00C4D4); color:#0A1424; border:0; border-radius:10px;
    padding:12px; font-size:14px; font-weight:800; cursor:pointer; font-family:inherit; }
  .cf-login-btn:disabled { opacity:.6; cursor:default; }
  .cf-login-legal { margin-top:10px; font-size:11.5px; opacity:.7; }
  .cf-login-legal a { color:#00A8B5; font-weight:600; }
`

/**
 * Componente reusable: 4 pasos checkout para Kit suelto y La Tienda.
 *
 * Props:
 *   tipo            — 'compra_unica' | 'productos_sueltos' | 'adicional_suscripcion'
 *   kitCodigo       — opcional, sólo si lleva un kit
 *   itemsIniciales  — array {sku, nombre, presentacion, precio_unit, cantidad}
 *   subtotalKit     — opcional, precio del kit (si tipo='compra_unica')
 *   esSuscriptor    — bool (true desactiva mensaje invitado)
 *   onCancelar      — callback () → void
 *   onCompletado    — callback (pedido) → void
 *   onPedidoCreado  — callback opcional (pedidoOData) → void, invocado JUSTO tras crear el pedido
 *                     (antes del redirect a MP / navegación). Lo usa /checkout v5 para vaciar el
 *                     carrito (CartContext) y evitar re-checkout del mismo pedido. Consumidores
 *                     viejos que no lo pasan → no-op.
 *   tituloHero      — string para el hero ("Probá nuestro Kit Familiar", etc)
 */
export default function CheckoutFlow({
  tipo, kitCodigo = null, itemsIniciales = [], subtotalKit = 0,
  esSuscriptor = false, direccionSuscriptor = null,
  onCancelar, onCompletado, onPedidoCreado,
  tituloHero = 'Tu pedido',
}) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  // Fix 7-jul-2026 (Nico): persistir paso + form en localStorage para no perder
  // el estado al navegar afuera y volver. Key incluye 'tipo' para no mezclar
  // estados entre checkout de tienda y checkout de kit.
  const STORAGE_KEY = `limpack_checkout_state_v1_${tipo || 'default'}`
  const [paso, setPaso] = useState(() => {
    try { return Number(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').paso) || 1 } catch { return 1 }
  })  // 1 resumen · 2 dirección · 3 día · 4 pago
  const [barrios, setBarrios] = useState([])
  const [items, setItems] = useState(itemsIniciales)
  const [coords, setCoords] = useState(null)
  const [coordsExactas, setCoordsExactas] = useState(false)
  const [geocodando, setGeocodando] = useState(false)
  const [validacionBarrio, setValidacionBarrio] = useState(null)  // { ok, sinCobertura, sugerenciaActivo, mensaje }
  const [editandoDireccion, setEditandoDireccion] = useState(false)  // suscriptor: cambiar dirección de siempre
  const [mostrarLoginInline, setMostrarLoginInline] = useState(false) // visitante: abrir <LoginInline> en paso Dirección
  const [opcionesFecha, setOpcionesFecha] = useState([])
  const [cargandoFechas, setCargandoFechas] = useState(false)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)
  const [perfilFacturacionId, setPerfilFacturacionId] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState(null)  // token Cloudflare Turnstile (visitor mode)
  const [proximaEntregaKit, setProximaEntregaKit] = useState(null)  // 'YYYY-MM-DD' próxima entrega del kit suscripto, para indicar "se suma a tu kit"

  const [form, setForm] = useState(() => {
    // Fix 7-jul-2026 (Nico): restaurar form persistido si existe
    const defaults = {
      // Dirección
      barrioId: '',
      direccion: '',   // string completo "Calle Número" — se calcula en base a calle+numero, se mantiene por compat
      calle: '',       // Fix 7-jul-2026 (Nico): separado en 2 inputs para no dejar avanzar sin número
      numero: '',      // (repartidor no puede llegar sin número exacto)
      apto: '',
      tipoVivienda: '',
      destinatarioNombre: '',
      destinatarioTelefono: '',
      // Día y horario
      fechaEntrega: '',          // 'YYYY-MM-DD'
      diaSemana: '',             // 'lunes' | etc
      fueraDeCorte: false,
      ventana: 'manana',
      // Pago
      metodoPago: 'transferencia',
      compradorNombre: profile?.nombre || '',
      compradorTelefono: profile?.phone || '',
      compradorEmail: profile?.email || user?.email || '',
      compradorPassword: '',  // solo para visitor signup on-the-fly
      notas: '',
    }
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').form
      if (saved && typeof saved === 'object') {
        // Nunca restaurar password persistido (seguridad)
        return { ...defaults, ...saved, compradorPassword: '' }
      }
    } catch { /* noop */ }
    return defaults
  })

  // Persistir paso + form en cada cambio (fix 7-jul-2026 Nico)
  useEffect(() => {
    try {
      const { compradorPassword, ...safeForm } = form  // no persistir password
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ paso, form: safeForm }))
    } catch { /* noop */ }
  }, [paso, form, STORAGE_KEY])

  // Cancelar limpia el estado persistido para no atrapar al user en el checkout
  function handleCancelarLimpiando() {
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('limpack_checkout_activo_v1')
      localStorage.removeItem('limpack_checkout_kit_v1')
    } catch { /* noop */ }
    onCancelar?.()
  }

  const esVisitor = !user

  // Cargar barrios al montar + precargar la huella antifraude de MP (device_id)
  useEffect(() => {
    getBarriosActivos().then(setBarrios)
    cargarZonasDesdeDB()  // precio por zona para el preview de envío (modelo unificado)
    initMpDeviceId()
  }, [])

  // Si es suscriptor, cargar próxima entrega futura del kit (para marcar "se suma a tu kit")
  useEffect(() => {
    if (!esSuscriptor || !user?.id) { setProximaEntregaKit(null); return }
    let cancel = false
    ;(async () => {
      try {
        const { data: cliente } = await supabase
          .from('clientes')
          .select('id')
          .eq('titular_id', user.id)
          .eq('estado', 'activa')
          .maybeSingle()
        if (cancel || !cliente?.id) return
        const { data: e } = await supabase
          .from('entregas')
          .select('fecha_programada')
          .eq('cliente_id', cliente.id)
          .in('estado', ['pendiente_pago', 'programada'])
          .gte('fecha_programada', new Date().toISOString().slice(0, 10))
          .order('fecha_programada', { ascending: true })
          .limit(1)
        if (cancel) return
        setProximaEntregaKit(e?.[0]?.fecha_programada || null)
      } catch (_) { /* ignore */ }
    })()
    return () => { cancel = true }
  }, [esSuscriptor, user?.id])

  // Fix 7-jul-2026 (Nico): parsear "18 de Julio 1234" en calle+numero
  // Regex: última secuencia de dígitos al final = número, resto = calle
  function parsearDireccion(str) {
    const s = (str || '').trim()
    const m = s.match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/)
    if (m) return { calle: m[1].trim(), numero: m[2].trim() }
    return { calle: s, numero: '' }
  }

  // Si es suscriptor, prerelleno con su dirección "de siempre"
  useEffect(() => {
    if (!direccionSuscriptor) return
    const { calle: calleParsed, numero: numeroParsed } = parsearDireccion(direccionSuscriptor.direccion || '')
    setForm(f => ({
      ...f,
      barrioId: direccionSuscriptor.barrioId || f.barrioId,
      direccion: direccionSuscriptor.direccion || f.direccion,
      calle: calleParsed || f.calle,
      numero: numeroParsed || f.numero,
      apto: direccionSuscriptor.apto || f.apto,
      tipoVivienda: direccionSuscriptor.tipoVivienda || f.tipoVivienda,
    }))
    if (direccionSuscriptor.lat != null && direccionSuscriptor.lng != null) {
      setCoords({ lat: direccionSuscriptor.lat, lng: direccionSuscriptor.lng })
      // El usuario tiene que confirmar manualmente el pin tildando el checkbox
      setCoordsExactas(false)
    }
  }, [direccionSuscriptor])

  // Sincronizar perfil del user logueado
  useEffect(() => {
    if (profile && !form.compradorNombre) {
      setForm(f => ({
        ...f,
        compradorNombre: profile.nombre || f.compradorNombre,
        compradorTelefono: profile.phone || f.compradorTelefono,
        compradorEmail: profile.email || user?.email || f.compradorEmail,
      }))
    }
  }, [profile, user])

  // Post-login inline (visitante que se logueó en el paso Dirección): hidratamos el form con los datos
  // de la cuenta SIN cambiar de paso (el carrito ya persiste en CartContext/localStorage). Al setear
  // la sesión, useAuth pasa a logueado → confirmar() usará el path cliente. Traemos nombre/tel/email de
  // profiles y dirección/apto/barrio del clientes activo (si tiene).
  async function handleLoginSuccess(loggedUser) {
    setMostrarLoginInline(false)
    const uid = loggedUser?.id
    if (!uid) return
    try {
      const { data: prof } = await supabase.from('profiles').select('nombre, phone, email').eq('id', uid).maybeSingle()
      const { data: cli } = await supabase.from('clientes')
        .select('direccion, apto, barrio_id')
        .or(`titular_id.eq.${uid},beneficiario_id.eq.${uid}`)
        .not('direccion', 'is', null)
        .order('created_at', { ascending: false }).limit(1).maybeSingle()
      setForm(f => ({
        ...f,
        compradorNombre: prof?.nombre || f.compradorNombre,
        compradorTelefono: prof?.phone || f.compradorTelefono,
        compradorEmail: prof?.email || loggedUser?.email || f.compradorEmail,
        barrioId: cli?.barrio_id || f.barrioId,
        direccion: cli?.direccion || f.direccion,
        apto: cli?.apto || f.apto,
      }))
    } catch { /* si falla la hidratación, el user completa a mano; ya está logueado */ }
  }

  const barrioElegido = barrios.find(b => b.id === form.barrioId)

  // Subtotal calculado
  const subtotalItems = useMemo(
    () => items.reduce((s, it) => s + Number(it.precio_unit) * Number(it.cantidad || 1), 0),
    [items]
  )
  const subtotal = subtotalItems + Number(subtotalKit || 0)

  // Cargar opciones de fecha cuando cambia barrio (incluye dias fuera de ruta)
  useEffect(() => {
    if (paso !== 3 || !form.barrioId) return
    setCargandoFechas(true)
    // Fix 7-jul-2026 (Nico): pasar diasAntes=1 (D-1) + margenMinHoras=8 (era 24
    // por default, contradice regla D-1: si el corte es hoy 16hs para entrega mañana
    // 10hs, hay 18h, no 24h. Filtraba la entrega del día siguiente aunque estaba
    // antes del corte). 8h = margen mínimo de armado + salida a ruta.
    proximasFechasEntrega({ barrioId: form.barrioId, maxOpciones: 8, diasAntes: 1, margenMinHoras: 8, soloEnRuta: false })
      .then(setOpcionesFecha)
      .finally(() => setCargandoFechas(false))
  }, [paso, form.barrioId])

  // Helper: calcular envio en preview (frontend)
  function calcEnvioPreview(opcion) {
    if (!barrioElegido || !opcion) return { costoEnvio: 0, enRuta: true }
    return calcularEnvio({
      esSuscriptor,
      tipo,
      barrio: barrioElegido,
      fecha: opcion.fechaEntrega,
      monto: subtotal,
      fueraDeCorte: opcion.fueraDeCorte,
    })
  }

  async function geocodear() {
    setErr(null); setCoordsExactas(false); setValidacionBarrio(null)
    if (!barrioElegido || !form.direccion.trim()) return
    setGeocodando(true)
    try {
      // Geocoding SIN forzar el barrio en la query (para que detecte el real)
      const r = await geocodificar(form.direccion)
      if (r) {
        setCoords({ lat: r.lat, lng: r.lng })
        // NO autoconfirmar — el cliente tiene que mirar el mapa y tildar el checkbox
        setCoordsExactas(false)
        // Validar el barrio por COORDENADAS (point-in-polygon oficial). Si el punto no cae
        // en ningún polígono (indeterminado), caer al método viejo por nombre de OSM.
        const vCoord = validarBarrioPorCoords(r.lat, r.lng, barrioElegido.nombre, barrios)
        setValidacionBarrio(vCoord.indeterminado
          ? validarBarrioConDireccion(r.barrioDetectado, barrioElegido.nombre, barrios)
          : vCoord)
        return
      }
      // Fallback: centroide del barrio
      const rb = await geocodificar(barrioElegido.nombre)
      if (rb) setCoords({ lat: rb.lat, lng: rb.lng })
    } finally { setGeocodando(false) }
  }

  function aplicarSugerencia() {
    if (!validacionBarrio?.sugerenciaActivo) return
    setForm(f => ({ ...f, barrioId: validacionBarrio.sugerenciaActivo.id }))
    setValidacionBarrio(null)
  }

  function toggleItemCantidad(sku, delta) {
    setItems(prev => prev
      .map(it => it.sku === sku ? { ...it, cantidad: Math.max(0, Number(it.cantidad || 1) + delta) } : it)
      .filter(it => it.cantidad > 0)
    )
  }

  function elegirFecha(opcion) {
    setForm(f => ({
      ...f,
      fechaEntrega: opcion.fechaEntrega,
      diaSemana: opcion.nombreDia,
      fueraDeCorte: opcion.fueraDeCorte,
    }))
  }

  async function confirmar() {
    setErr(null); setCreando(true)
    dbg('confirmar() · tap Pagar', {
      esVisitor, esSuscriptor, hasUser: !!user, userId: user?.id || null,
      itemsLen: items.length, subtotal, tipo, kitCodigo,
      barrioId: form.barrioId, dia: form.diaSemana, fechaEntrega: form.fechaEntrega, ventana: form.ventana,
      turnstile: !!turnstileToken, perfilFacturacionId: perfilFacturacionId || null,
    })
    try {
      if (!form.barrioId) throw new Error('Falta barrio')
      if (!form.direccion.trim()) throw new Error('Falta dirección')
      if (!form.fechaEntrega) throw new Error('Falta elegir día')
      if (!form.compradorNombre.trim()) throw new Error('Falta tu nombre')
      const tel = form.compradorTelefono.replace(/\D/g, '')
      if (tel.length < 8) throw new Error('Falta un WhatsApp válido (mínimo 8 dígitos)')
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.compradorEmail.trim())) {
        throw new Error('Falta un email válido')
      }

      // VISITOR: crear cuenta + pedido + MP Checkout Pro en una sola llamada (edge publica)
      // Migrado 2-jul-2026: vuelta a MP (era Plexo, ahora MP es el procesador único).
      if (esVisitor) {
        if (!form.compradorPassword || form.compradorPassword.length < 8) {
          throw new Error('Creá una contraseña de al menos 8 caracteres')
        }
        if (!turnstileToken) {
          throw new Error('Completá la verificación anti-bot antes de continuar')
        }
        const deviceId = await getMpDeviceId() // huella antifraude MP (best-effort, no bloquea)
        const { data: visitorData, error: visErr } = await supabase.functions.invoke('crear-pedido-publico', {
          body: {
            email: form.compradorEmail.trim().toLowerCase(),
            password: form.compradorPassword,
            nombre: form.compradorNombre.trim(),
            telefono: form.compradorTelefono,
            turnstile_token: turnstileToken,
            device_id: deviceId || undefined,
            utm: getUTM() || undefined, // atribución orgánico→tienda (26-jul)
            pedido: {
              tipo,
              kit_codigo: kitCodigo,
              items: items.map(it => ({
                sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
                precio_unit: Number(it.precio_unit), cantidad: Number(it.cantidad || 1),
                marca: it.marca,
              })),
              comprador_nombre: form.compradorNombre.trim(),
              comprador_telefono: form.compradorTelefono,
              comprador_email: form.compradorEmail.trim().toLowerCase(),
              entrega_barrio_id: form.barrioId,
              entrega_direccion: form.direccion,
              entrega_apto: form.apto,
              entrega_lat: coords?.lat,
              entrega_lng: coords?.lng,
              entrega_dia_semana: form.diaSemana,
              entrega_ventana: form.ventana,
              monto_subtotal: subtotal,
              costo_envio: costoEnvio,
              fecha_entrega_programada: form.fechaEntrega,
              notas: form.notas,
            },
          },
        })
        dbg('VISITOR · crear-pedido-publico resp', { visErr: visErr?.message || null, dataError: visitorData?.error || null, hasInitPoint: !!(visitorData?.init_point || visitorData?.plexo_uri) })
        if (visErr) throw visErr
        if (visitorData?.error) throw new Error(visitorData.error + (visitorData.detalle ? ': ' + JSON.stringify(visitorData.detalle) : ''))
        // Redirigir al checkout MercadoPago
        const initPoint = visitorData.init_point || visitorData.plexo_uri
        if (!initPoint) throw new Error('MercadoPago no devolvió URL de pago')
        dbg('VISITOR · window.location.href =', initPoint)
        // Guardar email del visitor en localStorage para mostrar mensaje al volver
        try { localStorage.setItem('limpack_visitor_email', form.compradorEmail.trim().toLowerCase()) } catch {}
        onPedidoCreado?.(visitorData)  // vaciar carrito v5 antes de salir a MP (evita re-checkout)
        window.location.href = initPoint
        return
      }

      dbg('LOGUEADO · llamando crearPedido (cliente_crear_pedido)', { tipo, kitCodigo, itemsLen: items.length, barrioId: form.barrioId, metodoPago: 'mercadopago' })
      const pedido = await crearPedido({
        tipo,
        kitCodigo,
        items: items.map(it => ({
          sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
          precio_unit: Number(it.precio_unit), cantidad: Number(it.cantidad || 1),
          marca: it.marca,
        })),
        barrioId: form.barrioId,
        direccion: form.direccion,
        apto: form.apto,
        lat: coords?.lat,
        lng: coords?.lng,
        diaSemana: form.diaSemana,
        ventana: form.ventana,
        fechaEntrega: form.fechaEntrega,
        fueraDeCorte: form.fueraDeCorte,
        destinatarioNombre: form.destinatarioNombre,
        destinatarioTelefono: form.destinatarioTelefono,
        compradorNombre: form.compradorNombre,
        compradorTelefono: form.compradorTelefono,
        compradorEmail: form.compradorEmail,
        metodoPago: 'mercadopago',  // migrado a MP 2-jul-2026 (procesador único)
        notas: form.notas,
        perfilFacturacionId: perfilFacturacionId,
      })
      // Limpiar estado persistido del checkout (fix 7-jul-2026 Nico)
      try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem('limpack_checkout_activo_v1')
        localStorage.removeItem('limpack_checkout_kit_v1')
      } catch { /* noop */ }
      dbg('LOGUEADO · crearPedido OK', { pedidoId: pedido?.id || null, pedidoRaw: pedido })
      // Redirigir a la pantalla de pago MP con el pedido_id
      onPedidoCreado?.(pedido)  // vaciar carrito v5 tras crear el pedido (evita re-checkout)
      if (pedido?.id) {
        dbg('LOGUEADO · navigate → /cliente/pagar-pedido/' + pedido.id)
        navigate(`/cliente/pagar-pedido/${pedido.id}`, { replace: true })
      } else {
        dbgWarn('LOGUEADO · pedido SIN id → onCompletado (va a /gracias)', pedido)
        onCompletado?.(pedido)
      }
    } catch (e) {
      dbgWarn('confirmar() · CATCH', e?.message || String(e), e)
      setErr(e.message || String(e))
      setCreando(false)
      // Dejar rastro del fallo en la cuenta del cliente para poder diagnosticarlo después desde
      // la ficha (antes el error moría en el navegador del usuario y quedábamos a ciegas).
      try {
        supabase.rpc('registrar_error_mi_carrito', {
          p_error: `[checkout ${esVisitor ? 'visitante' : 'logueado'}] ${e?.message || String(e)}`,
          p_paso: 'checkout_confirmar',
        })
      } catch { /* best-effort */ }
    }
  }

  // Costo envío del fecha elegida (para mostrar en paso 4)
  const opcionElegida = opcionesFecha.find(o => o.fechaEntrega === form.fechaEntrega)
  const previewElegida = opcionElegida ? calcEnvioPreview(opcionElegida) : { costoEnvio: 0 }
  const costoEnvio = previewElegida.costoEnvio
  const totalFinal = subtotal + costoEnvio

  return (
    <div className="cf-shell">
      {/* HERO */}
      <div className="cf-hero">
        <div className="cf-hero-pill">
          <span className="cf-hero-dot"></span>
          Paso {paso} de 4 · Tu pedido
        </div>
        <h1>{tituloHero}</h1>
      </div>

      {/* Stepper — pasos previos clickeables para volver atras */}
      <div className="cf-stepper">
        {['Resumen', 'Dirección', 'Día', 'Pagar'].map((label, i) => {
          const idx = i + 1
          const isDone = paso > idx
          const isActive = paso === idx
          const clickeable = isDone  // solo pasos completados
          return (
            <button
              type="button"
              key={i}
              disabled={!clickeable}
              onClick={() => clickeable && setPaso(idx)}
              className={`cf-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
              style={{
                background: 'transparent', border: 0, font: 'inherit',
                cursor: clickeable ? 'pointer' : 'default',
                padding: 0,
              }}>
              <div className="cf-step-num">{isDone ? '✓' : idx}</div>
              <div className="cf-step-label">{label}</div>
            </button>
          )
        })}
      </div>

      {/* PASO 1: Resumen items */}
      {paso === 1 && (
        <div className="cf-card">
          <h2 className="cf-h2">Lo que llevás</h2>
          {kitCodigo && (
            <div className="cf-kit-row">
              <div>
                <div className="cf-kit-tag">📦 KIT</div>
                <div className="cf-kit-nombre">{kitCodigo.replace(/_/g, ' ').toUpperCase()}</div>
                <div className="cf-kit-sub">Kit completo · una sola vez</div>
              </div>
              <div className="cf-kit-precio">${Number(subtotalKit).toLocaleString('es-UY')}</div>
            </div>
          )}
          {items.length > 0 && (
            <ul className="cf-items">
              {items.map(it => (
                <li key={it.sku}>
                  <div className="cf-item-info">
                    <strong>{it.nombre}</strong>
                    {it.presentacion && <span> · {it.presentacion}</span>}
                    {it.marca && <span className="cf-item-marca">{it.marca}</span>}
                  </div>
                  <div className="cf-item-cant">
                    <button onClick={() => toggleItemCantidad(it.sku, -1)} aria-label="Quitar uno">−</button>
                    <span>{it.cantidad}</span>
                    <button onClick={() => toggleItemCantidad(it.sku, +1)} aria-label="Agregar uno">+</button>
                  </div>
                  <div className="cf-item-precio">${(Number(it.precio_unit) * Number(it.cantidad)).toLocaleString('es-UY')}</div>
                </li>
              ))}
            </ul>
          )}
          {(!kitCodigo && items.length === 0) && (
            <p className="cf-empty">Tu carrito está vacío. <button className="cf-link" onClick={handleCancelarLimpiando}>← Volver</button></p>
          )}

          <div className="cf-totales">
            <div><span>Subtotal</span><strong>${subtotal.toLocaleString('es-UY')}</strong></div>

            {/* Indicador envío gratis — solo compra suelta (los kits ya llevan envío gratis el día de ruta).
                Umbral = ENVIO_GRATIS_DESDE ($1.000) de CartContext. */}
            {!kitCodigo && subtotal > 0 && (
              subtotal < ENVIO_GRATIS_DESDE ? (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)',
                  color: '#C5A059', fontSize: 13, fontWeight: 600,
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px',
                }}>
                  <span>🚚 Faltan <strong>${(ENVIO_GRATIS_DESDE - subtotal).toLocaleString('es-UY')}</strong> para envío gratis</span>
                  <button type="button" onClick={() => navigate('/catalogo')} style={{
                    background: 'transparent', border: 0, color: '#C5A059', textDecoration: 'underline',
                    cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0, fontFamily: 'inherit',
                  }}>Agregar más productos →</button>
                </div>
              ) : (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                  color: '#10B981', fontSize: 13, fontWeight: 700,
                }}>🎉 ¡Envío gratis conseguido!</div>
              )
            )}

            <div className="cf-totales-info">Precios con IVA incluido. El envío según tu barrio se define en el paso Día.</div>
          </div>

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={handleCancelarLimpiando}>← Cancelar</button>
            <button className="cf-btn-next" onClick={() => setPaso(2)} disabled={subtotal <= 0}>Siguiente →</button>
          </div>
        </div>
      )}

      {/* PASO 2: Dirección */}
      {paso === 2 && (
        <div className="cf-card">
          <h2 className="cf-h2">¿A dónde te lo enviamos?</h2>

          {/* Visitante sin sesión: login inline discreto → hidrata el form sin reiniciar el wizard */}
          {esVisitor && (
            mostrarLoginInline
              ? <LoginInline onSuccess={handleLoginSuccess} onCancel={() => setMostrarLoginInline(false)} />
              : (
                <button type="button" className="cf-login-link" onClick={() => setMostrarLoginInline(true)}>
                  ¿Ya sos cliente? <strong>Iniciá sesión</strong> y traemos tus datos
                </button>
              )
          )}

          {/* Suscriptor: cartel "tu dirección de siempre" — si no cliquea cambiar, no muestra el form */}
          {direccionSuscriptor && !editandoDireccion ? (
            <>
              <div className="cf-direccion-siempre">
                <div className="cf-direccion-siempre-icon">📍</div>
                <div className="cf-direccion-siempre-body">
                  <div className="cf-direccion-siempre-tag">Tu dirección de siempre</div>
                  <div className="cf-direccion-siempre-linea">
                    {direccionSuscriptor.direccion}
                    {direccionSuscriptor.apto ? `, ${direccionSuscriptor.apto}` : ''}
                  </div>
                  <div className="cf-direccion-siempre-barrio">
                    {direccionSuscriptor.barrioNombre}
                  </div>
                </div>
              </div>
              <button type="button" className="cf-link-cambiar" onClick={() => setEditandoDireccion(true)}>
                ✏️ Enviarlo a otra dirección
              </button>

              <div className="cf-nav">
                <button className="cf-btn-back" onClick={() => setPaso(1)}>← Atrás</button>
                <button className="cf-btn-next" onClick={() => setPaso(3)}
                  disabled={!form.barrioId || !form.direccion.trim()}>
                  Siguiente →
                </button>
              </div>
            </>
          ) : (
          <>
          {direccionSuscriptor && (
            <div className="cf-aviso-otra-direccion">
              <span>📦 Vas a enviarlo a otra dirección distinta a tu dirección de siempre.</span>
              <button type="button" onClick={() => {
                setEditandoDireccion(false)
                // restaurar dirección de siempre
                setForm(f => ({
                  ...f,
                  barrioId: direccionSuscriptor.barrioId || '',
                  direccion: direccionSuscriptor.direccion || '',
                  apto: direccionSuscriptor.apto || '',
                  tipoVivienda: direccionSuscriptor.tipoVivienda || '',
                }))
                if (direccionSuscriptor.lat != null) {
                  setCoords({ lat: direccionSuscriptor.lat, lng: direccionSuscriptor.lng })
                  // Igual el suscriptor tiene que confirmar el pin tildando el checkbox
                  setCoordsExactas(false)
                }
                setValidacionBarrio(null)
              }}>← Usar la de siempre</button>
            </div>
          )}

          <div className="cf-row-2">
            <Field label="Barrio" required>
              <select value={form.barrioId} onChange={e => setForm({ ...form, barrioId: e.target.value })}>
                <option value="">— elegir —</option>
                {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </Field>
            <Field label="Tipo de vivienda">
              <select value={form.tipoVivienda} onChange={e => setForm({ ...form, tipoVivienda: e.target.value })}>
                <option value="">— elegir —</option>
                <option value="casa">Casa</option>
                <option value="edificio">Edificio</option>
                <option value="oficina">Oficina</option>
              </select>
            </Field>
          </div>

          {!form.barrioId && barrios.length > 0 && (
            <div className="cf-info">Por ahora cubrimos: {barrios.map(b => b.nombre).join(', ')}.</div>
          )}

          {/* Fix 7-jul-2026 (Nico): separado en 2 campos. El repartidor no puede
              llegar sin número exacto — antes el input era único y permitía avanzar
              con "arocena" sin número. Se compone form.direccion="{calle} {numero}"
              para no romper el resto del flow (geocodificación, guardado, validación). */}
          <div className="cf-row-calle-nro">
            <Field label="Calle" required>
              <input type="text" value={form.calle}
                onChange={e => {
                  const calle = e.target.value
                  setForm(f => ({ ...f, calle, direccion: `${calle.trim()} ${(f.numero || '').trim()}`.trim() }))
                }}
                onBlur={() => { if (form.calle && form.numero) geocodear() }}
                placeholder="Ej: Av. Brasil" />
            </Field>
            <Field label="Número" required>
              <input type="text" inputMode="numeric" value={form.numero}
                onChange={e => {
                  const numero = e.target.value
                  setForm(f => ({ ...f, numero, direccion: `${(f.calle || '').trim()} ${numero.trim()}`.trim() }))
                }}
                onBlur={() => { if (form.calle && form.numero) geocodear() }}
                placeholder="2500" />
            </Field>
            <Field label="Apto / Piso">
              <input type="text" value={form.apto}
                onChange={e => setForm({ ...form, apto: e.target.value })} placeholder="Ej: 501" />
            </Field>
          </div>

          <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} className="cf-btn-secondary">
            {geocodando ? 'Buscando…' : coords ? 'Recargar mapa' : 'Verificar en mapa'}
          </button>

          {/* Validación barrio detectado vs barrio elegido */}
          {validacionBarrio && !validacionBarrio.ok && (
            <div className={`cf-warn-barrio ${validacionBarrio.sinCobertura ? 'is-bloqueo' : 'is-warn'}`}>
              <div className="cf-warn-barrio-icon">{validacionBarrio.sinCobertura ? '🚫' : '⚠️'}</div>
              <div className="cf-warn-barrio-body">
                <strong>{validacionBarrio.mensaje}</strong>
                {validacionBarrio.sinCobertura ? (
                  <>
                    <p>Por ahora no llegamos a esa zona. Si querés, te avisamos cuando lleguemos:</p>
                    <a
                      href={`https://wa.me/59898610001?text=${encodeURIComponent('Hola Limpack! Vivo en ' + form.direccion + ' y todavía no llegan a mi zona. ¿Me avisan cuando empiecen?')}`}
                      target="_blank" rel="noopener" className="cf-warn-cta"
                    >
                      📲 Avisame por WhatsApp
                    </a>
                  </>
                ) : validacionBarrio.sugerenciaActivo ? (
                  <>
                    <p>Para continuar, cambiá el barrio elegido o corregí la dirección.</p>
                    <button type="button" className="cf-warn-cta" onClick={aplicarSugerencia}>
                      ✓ Cambiar a {validacionBarrio.sugerenciaActivo.nombre}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          )}

          {coords && !validacionBarrio?.sinCobertura && (
            <>
              <div style={{ marginTop: 12 }}>
                <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false); if (barrioElegido) setValidacionBarrio(validarBarrioPorCoords(lat, lng, barrioElegido.nombre, barrios)) }} height={220} />
              </div>
              <label className={`cf-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} />
                <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Confirmá el pin antes de seguir'}</span>
              </label>
            </>
          )}

          {tipo === 'adicional_suscripcion' && (
            <>
              <h3 className="cf-h3">¿Es para otra persona?</h3>
              <div className="cf-row-2">
                <Field label="Destinatario (opcional)">
                  <input type="text" value={form.destinatarioNombre}
                    onChange={e => setForm({ ...form, destinatarioNombre: e.target.value })}
                    placeholder="Mamá, oficina, etc." />
                </Field>
                <Field label="Teléfono destinatario">
                  <input type="tel" value={form.destinatarioTelefono}
                    onChange={e => setForm({ ...form, destinatarioTelefono: e.target.value })}
                    placeholder="099 xxx xxx" />
                </Field>
              </div>
            </>
          )}

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(1)}>← Atrás</button>
            <button className="cf-btn-next"
              onClick={() => setPaso(3)}
              disabled={
                !form.barrioId
                || !form.calle.trim()   // Fix 7-jul-2026: exigir calle Y número por separado
                || !form.numero.trim()  // (antes solo pedía form.direccion.trim() → pasaba con solo la calle)
                || (validacionBarrio && !validacionBarrio.ok)
                // Exigir confirmación del pin SIEMPRE que el barrio esté en cobertura
                // (si no hay cobertura, el siguiente paso de igual modo va a frenar)
                || (!validacionBarrio?.sinCobertura && !coordsExactas)
              }>
              Siguiente →
            </button>
          </div>
          </>
          )}
        </div>
      )}

      {/* PASO 3: Día */}
      {paso === 3 && (
        <div className="cf-card">
          <h2 className="cf-h2">¿Cuándo te lo enviamos?</h2>

          {/* Banner compacto suscriptor / no-suscriptor */}
          {esSuscriptor ? (
            <div className="cf-banner-susc">
              <div className="cf-banner-icon">⭐</div>
              <div>
                <strong>Sos suscriptor — envío gratis los días de tu ruta.</strong>
                <span> En <strong>{barrioElegido?.nombre}</strong> pasamos{' '}
                <strong style={{ textTransform: 'capitalize' }}>{(barrioElegido?.dias || []).join(' y ')}</strong>.</span>
                <div className="cf-banner-corte">
                  ⏰ Corte para pedir: <strong>16hs del día hábil anterior</strong>. Para entregas del lunes, el corte es el viernes 16hs.
                </div>
              </div>
            </div>
          ) : (
            <div className="cf-banner-info">
              <div className="cf-banner-icon">💡</div>
              <div>
                <strong>En {barrioElegido?.nombre} pasamos{' '}
                  <span style={{ textTransform: 'capitalize' }}>{(barrioElegido?.dias || []).join(' y ')}</span>.
                </strong>
                <div className="cf-banner-corte">
                  ⏰ Corte para pedir: <strong>16hs del día hábil anterior</strong>.
                </div>
              </div>
            </div>
          )}

          {cargandoFechas && <div className="cf-loading">Buscando próximas fechas…</div>}

          {!cargandoFechas && opcionesFecha.length === 0 && (
            <div className="cf-info">No hay fechas disponibles para este barrio.</div>
          )}

          {/* Selector custom compacto (controlamos font/spacing, no el picker nativo) */}
          {!cargandoFechas && opcionesFecha.length > 0 && (
            <>
              {/* Banner sugerencia si el suscriptor puede sumar al kit del mes */}
              {esSuscriptor && proximaEntregaKit && opcionesFecha.some(o => o.fechaEntrega === proximaEntregaKit) && form.fechaEntrega !== proximaEntregaKit && (
                <div className="cf-sugerencia-kit">
                  <span className="cf-sug-icon">🎁</span>
                  <div className="cf-sug-body">
                    <strong>¿Lo sumás a tu próximo kit?</strong>
                    <p>El <b>{formatFechaCorta(proximaEntregaKit)}</b> ya pasamos con tu Kit del mes. Si elegís ese día va todo junto y se cobra con tu suscripción.</p>
                  </div>
                  <button type="button" className="cf-sug-btn" onClick={() => {
                    const opc = opcionesFecha.find(o => o.fechaEntrega === proximaEntregaKit)
                    if (opc) elegirFecha(opc)
                  }}>Sumar al kit →</button>
                </div>
              )}

              <Field label="Día de entrega" required>
                <FechaDropdown
                  opciones={opcionesFecha}
                  valor={form.fechaEntrega}
                  onSeleccionar={elegirFecha}
                  calcEnvio={(o) => calcEnvioPreview(o).costoEnvio}
                  proximaEntregaKit={esSuscriptor ? proximaEntregaKit : null}
                />
              </Field>

              {/* Resumen del día elegido */}
              {opcionElegida && (
                <div className={`cf-fecha-resumen ${opcionElegida.enRuta ? 'is-en-ruta' : 'is-fuera'}`}>
                  <div className="cf-fecha-resumen-info">
                    <div className="cf-fecha-resumen-tag">
                      {opcionElegida.enRuta ? '🟢 En tu ruta normal' : '🟡 Viaje especial fuera de ruta'}
                    </div>
                    <div className="cf-fecha-resumen-dia">{formatFechaLarga(opcionElegida.fechaEntrega)}</div>
                    {/* Cross-sell: si el día coincide con la próxima entrega del kit, mostrar que se suman */}
                    {esSuscriptor && proximaEntregaKit && opcionElegida.fechaEntrega === proximaEntregaKit && (
                      <div className="cf-fecha-resumen-kit-match">
                        🎁 <strong>Se suma a tu kit del mes</strong> — llegan juntos en la misma bolsa
                      </div>
                    )}
                  </div>
                  <div className="cf-fecha-resumen-precio">
                    {previewElegida.costoEnvio > 0
                      ? <span>+${previewElegida.costoEnvio.toLocaleString('es-UY')} envío</span>
                      : <span className="cf-monto-ok">Envío gratis</span>}
                  </div>
                </div>
              )}
            </>
          )}

          <Field label="Ventana horaria">
            <select value={form.ventana} onChange={e => setForm({ ...form, ventana: e.target.value })}>
              {VENTANAS_HORARIAS.map(v => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
          </Field>

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(2)}>← Atrás</button>
            <button className="cf-btn-next" onClick={() => setPaso(4)} disabled={!form.fechaEntrega}>Siguiente →</button>
          </div>
        </div>
      )}

      {/* PASO 4: Pagar */}
      {paso === 4 && (
        <div className="cf-card">
          <h2 className="cf-h2">Confirmá y elegí cómo pagar</h2>

          <div className="cf-h3-row">
            <h3 className="cf-h3">Tus datos</h3>
            {esSuscriptor && profile && (
              <span className="cf-pill-cuenta">✓ Datos de tu cuenta</span>
            )}
          </div>
          <div className="cf-row-2">
            <Field label="Nombre completo" required>
              <input type="text" required value={form.compradorNombre}
                onChange={e => setForm({ ...form, compradorNombre: e.target.value })} />
            </Field>
            <Field label="WhatsApp" required>
              <input type="tel" required value={form.compradorTelefono}
                onChange={e => setForm({ ...form, compradorTelefono: e.target.value })}
                placeholder="099 xxx xxx" />
            </Field>
          </div>
          <Field label="Email" required>
            <input type="email" required value={form.compradorEmail}
              onChange={e => setForm({ ...form, compradorEmail: e.target.value })}
              placeholder="tu@email.com" />
          </Field>

          {esVisitor && (
            <>
              <Field label="Creá una contraseña" required>
                <PasswordInputConFortaleza
                  value={form.compradorPassword}
                  onChange={(v) => setForm({ ...form, compradorPassword: v })}
                />
              </Field>
              <div className="cf-visitor-aviso" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}>
                ✨ Te creamos tu cuenta automáticamente. Después del pago te mandamos un email para activarla y poder ver tus pedidos.
              </div>
              {/* Verificación anti-bot Cloudflare Turnstile (solo visitor) */}
              <TurnstileWidget action="checkout-publico" onToken={setTurnstileToken} />
            </>
          )}

          <p className="cf-hint">
            Necesitamos WhatsApp y email para confirmar el pedido y avisarte cuando salga el repartidor.
          </p>

          <h3 className="cf-h3">Resumen</h3>
          <div className="cf-resumen">
            <Row label="Subtotal">${subtotal.toLocaleString('es-UY')}</Row>
            <Row label="Envío">
              {costoEnvio > 0
                ? <strong>${costoEnvio.toLocaleString('es-UY')}</strong>
                : <strong style={{ color: '#15803d' }}>Gratis</strong>}
            </Row>
            <Row label="Día">
              {opcionElegida
                ? <strong>{formatFechaLarga(opcionElegida.fechaEntrega)}</strong>
                : '—'}
            </Row>
            <Row label="Horario">
              <strong>{labelVentana(form.ventana)}</strong>
            </Row>
            <Row label="Total" big>${totalFinal.toLocaleString('es-UY')}</Row>
            <div style={{ fontSize: 11, color: 'var(--cf-muted)', textAlign: 'right', paddingTop: 4 }}>
              Todos los precios con IVA incluido
            </div>
          </div>

          {/* Facturación: obligatorio si está logueado. Visitors quedan como CF al nombre del comprador. */}
          {user && (
            <>
              <h3 className="cf-h3">Facturación</h3>
              <div style={{ marginBottom: 16 }}>
                <BloqueFacturacion
                  value={perfilFacturacionId}
                  onChange={setPerfilFacturacionId}
                />
              </div>
            </>
          )}

          <h3 className="cf-h3">Método de pago</h3>
          <div style={{
            padding: '14px 16px',
            background: '#ecfdf5', border: '1px solid #a7f3d0',
            borderRadius: 12, color: '#065f46', fontSize: 13, lineHeight: 1.5,
            marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <img src="/mp-logo.svg" alt="MercadoPago" width="80" style={{ height: 'auto' }} />
              <strong>🔒 Pago seguro · Procesado por MercadoPago</strong>
            </div>
            Al confirmar te llevamos a una página segura donde elegís cómo pagar: tarjetas (Visa, Mastercard, OCA, AmEx, Cabal), banca digital, Redpagos, Abitab, transferencia bancaria y cuotas sin recargo.
          </div>

          <Field label="Notas para el repartidor (opcional)">
            <textarea rows="2" value={form.notas}
              onChange={e => setForm({ ...form, notas: e.target.value })}
              placeholder="Llamar al portero, dejar en recepción, etc." />
          </Field>

          {err && <div className="cf-err">{err}</div>}

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(3)}>← Atrás</button>
            <button
              className="cf-btn-next"
              onClick={confirmar}
              disabled={creando || (user && !perfilFacturacionId)}
              title={user && !perfilFacturacionId ? 'Elegí cómo te facturamos antes de continuar' : ''}
            >
              {creando ? 'Creando pedido…' : `💳 Confirmar y pagar · $${totalFinal.toLocaleString('es-UY')}`}
            </button>
          </div>
        </div>
      )}

      {/* Aviso de privacidad — visible en los 4 pasos */}
      <div className="cf-privacy">
        🔒 Tus datos están protegidos. Solo los usamos para procesar tu pedido y nunca los compartimos con terceros.
      </div>

      <style>{styles}</style>
      {/* Siempre montado: incluye el estilo del botón "¿Ya sos cliente?", que se muestra aunque el
          formulario de login esté cerrado (antes el CSS venía dentro de <LoginInline> y el botón
          quedaba sin estilo). */}
      <style>{LOGIN_INLINE_CSS}</style>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <label className="cf-field">
      <b>{label} {required && <span className="cf-req">*</span>}</b>
      {children}
    </label>
  )
}

// Evalúa fortaleza de contraseña. Devuelve {score 0-4, label, color, hints[], valid}.
// Mínimo aceptable: 8 chars + (letra y número).
function evaluarFortalezaPassword(pwd) {
  const p = String(pwd || '')
  const len = p.length
  let score = 0
  if (len >= 8) score++
  if (/[a-zA-Z]/.test(p)) score++
  if (/[0-9]/.test(p)) score++
  if (len >= 12 || /[^a-zA-Z0-9]/.test(p)) score++

  const hints = []
  if (len < 8) hints.push(`${8 - len} carácter${(8 - len) === 1 ? '' : 'es'} más`)
  if (!/[a-zA-Z]/.test(p)) hints.push('al menos una letra')
  if (!/[0-9]/.test(p)) hints.push('al menos un número')

  const labels = ['', 'Débil', 'Aceptable', 'Buena', 'Fuerte']
  const colors = ['#E2E8F0', '#EF4444', '#F59E0B', '#10B981', '#059669']

  return {
    score,
    label: labels[Math.min(score, 4)] || '',
    color: colors[Math.min(score, 4)] || '#E2E8F0',
    hints,
    // Aceptable mínimo para enviar: 8+ chars + al menos letra + al menos número
    valid: len >= 8 && /[a-zA-Z]/.test(p) && /[0-9]/.test(p),
  }
}

function PasswordInputConFortaleza({ value, onChange }) {
  const [showPwd, setShowPwd] = useState(false)
  const f = evaluarFortalezaPassword(value)
  const v = value || ''
  return (
    <>
      <div style={{ position: 'relative' }}>
        <input
          type={showPwd ? 'text' : 'password'}
          required
          minLength={8}
          autoComplete="new-password"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Mínimo 8 caracteres"
          style={{ paddingRight: 44, width: '100%' }}
        />
        <button
          type="button"
          onClick={() => setShowPwd((s) => !s)}
          aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 0, cursor: 'pointer', padding: 6,
            fontSize: 16, lineHeight: 1, color: '#64748B',
          }}
        >{showPwd ? '🙈' : '👁'}</button>
      </div>
      {v.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${f.score * 25}%`,
                height: '100%',
                background: f.color,
                transition: 'width .18s, background .18s',
              }} />
            </div>
            <span style={{ fontSize: 11.5, color: f.color, fontWeight: 700, minWidth: 70, textAlign: 'right' }}>
              {f.label}
            </span>
          </div>
          {f.hints.length > 0 ? (
            <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 4 }}>
              Falta: {f.hints.join(' · ')}
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: '#10B981', marginTop: 4, fontWeight: 600 }}>
              ✓ Cumple lo mínimo
            </div>
          )}
        </div>
      )}
    </>
  )
}

function Row({ label, children, big }) {
  return (
    <div className={`cf-row ${big ? 'is-big' : ''}`}>
      <span>{label}</span>
      <span className="cf-row-val">{children}</span>
    </div>
  )
}

function formatFechaLarga(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
}

function formatFechaCorta(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** Dropdown custom — sustituye al <select> nativo (que en Android es enorme).
 *  Trigger compacto + panel scrolleable abajo, con grupos En Ruta / Fuera de Ruta. */
function FechaDropdown({ opciones, valor, onSeleccionar, calcEnvio, proximaEntregaKit = null }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('touchstart', onDoc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('touchstart', onDoc)
    }
  }, [open])

  const enRuta = opciones.filter(o => o.enRuta)
  const fueraRuta = opciones.filter(o => !o.enRuta)
  const elegida = opciones.find(o => o.fechaEntrega === valor)
  const matchKit = (o) => proximaEntregaKit && o.fechaEntrega === proximaEntregaKit

  function pick(o) {
    onSeleccionar(o)
    setOpen(false)
  }

  return (
    <div className="cf-dd" ref={wrapRef}>
      <button type="button" className={`cf-dd-trigger ${open ? 'is-open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="cf-dd-trigger-text">
          {elegida
            ? <>{elegida.enRuta ? '🟢' : '🟡'} {formatFechaCorta(elegida.fechaEntrega)} · {(() => {
                const env = calcEnvio(elegida)
                return env > 0 ? `+$${env.toLocaleString('es-UY')}` : 'GRATIS'
              })()}</>
            : <span className="cf-dd-placeholder">— Elegí un día —</span>
          }
        </span>
        <span className="cf-dd-caret">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="cf-dd-panel">
          {enRuta.length > 0 && (
            <>
              <div className="cf-dd-grupo">🟢 Tu ruta normal · más barato</div>
              {enRuta.map(o => {
                const env = calcEnvio(o)
                const sel = o.fechaEntrega === valor
                const isKit = matchKit(o)
                return (
                  <button key={o.fechaEntrega} type="button" className={`cf-dd-opt ${sel ? 'is-sel' : ''} ${isKit ? 'is-kit' : ''}`} onClick={() => pick(o)}>
                    <span className="cf-dd-opt-dia">
                      {isKit && <span className="cf-dd-kit-badge">🎁 JUNTO A TU KIT</span>}
                      {formatFechaCorta(o.fechaEntrega)}
                    </span>
                    <span className={`cf-dd-opt-precio ${env > 0 ? '' : 'is-free'}`}>
                      {env > 0 ? `+$${env.toLocaleString('es-UY')}` : 'GRATIS'}
                    </span>
                  </button>
                )
              })}
            </>
          )}
          {fueraRuta.length > 0 && (
            <>
              <div className="cf-dd-grupo">🟡 Otros días · viaje especial</div>
              {fueraRuta.map(o => {
                const env = calcEnvio(o)
                const sel = o.fechaEntrega === valor
                return (
                  <button key={o.fechaEntrega} type="button" className={`cf-dd-opt ${sel ? 'is-sel' : ''}`} onClick={() => pick(o)}>
                    <span className="cf-dd-opt-dia">⚡ {formatFechaCorta(o.fechaEntrega)}</span>
                    <span className="cf-dd-opt-precio">+${env.toLocaleString('es-UY')}</span>
                  </button>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const styles = `
:root {
  --cf-navy:  #1a3461;
  --cf-blue:  #1a5cb8;
  --cf-teal:  #00A8B5;
  --cf-cream: #F8FAFC;
  --cf-line:  #e2e8f0;
  --cf-muted: #64748b;
}
.cf-shell { max-width: 760px; margin: 0 auto; padding: 24px 16px 60px; font-family: 'Inter', system-ui, sans-serif; color: var(--cf-navy) }

/* Hero */
.cf-hero { text-align: center; margin-bottom: 22px; padding: 32px 16px; background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00A8B5 100%); color: #fff; border-radius: 18px }
.cf-hero-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.18); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 12px }
.cf-hero-dot { width: 7px; height: 7px; background: var(--cf-teal); border-radius: 50%; animation: pulse 1.6s infinite }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.cf-hero h1 { font-size: clamp(24px, 5vw, 36px); font-weight: 900; letter-spacing: -.02em; margin: 0 }

/* Stepper */
.cf-stepper { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 18px }
.cf-step { text-align: center; opacity: .45 }
.cf-step.active, .cf-step.done { opacity: 1 }
.cf-step-num { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #e2e8f0; color: #64748b; font-weight: 800; font-size: 13px; margin-bottom: 4px }
.cf-step.active .cf-step-num { background: var(--cf-teal); color: var(--cf-navy) }
.cf-step.done .cf-step-num { background: #22c55e; color: #fff }
.cf-step-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em }
.cf-step.active .cf-step-label { color: var(--cf-navy) }

/* Card */
.cf-card { background: #fff; border-radius: 18px; padding: 24px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05) }
.cf-h2 { font-size: 22px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.01em; margin: 0 0 18px }
.cf-h3 { font-size: 14px; font-weight: 800; color: var(--cf-navy); letter-spacing: .02em; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: .08em }
.cf-h3-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin: 22px 0 10px }
.cf-h3-row .cf-h3 { margin: 0 }
.cf-pill-cuenta { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; background: rgba(0,196,212,.12); color: #0e7490; font-size: 11px; font-weight: 800; letter-spacing: .04em }
.cf-visitor-aviso { background: linear-gradient(135deg, rgba(0,196,212,.10), rgba(245,158,11,.08)); border: 1px solid rgba(0,196,212,.30); border-radius: 10px; padding: 12px 14px; font-size: 12px; color: var(--cf-navy); margin-bottom: 12px; line-height: 1.5 }
.cf-hint { font-size: 12px; color: var(--cf-muted); margin: 6px 0 14px; line-height: 1.5; font-style: italic }
.cf-sub { color: #475569; font-size: 14px; margin: 0 0 18px }
.cf-loading { color: var(--cf-muted); font-size: 14px; padding: 16px 0 }

/* Items */
.cf-items { list-style: none; padding: 0; margin: 12px 0 16px; display: flex; flex-direction: column; gap: 10px }
.cf-items li { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 12px; padding: 12px; background: #f8fafc; border: 1px solid var(--cf-line); border-radius: 12px; font-size: 14px }
.cf-item-info strong { color: var(--cf-navy) }
.cf-item-info span { color: var(--cf-muted) }
.cf-item-marca { display: inline-block; font-size: 11px; padding: 2px 8px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; margin-left: 6px }
.cf-item-cant { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; padding: 4px }
.cf-item-cant button { width: 26px; height: 26px; border-radius: 50%; border: 0; background: #f1f5f9; color: var(--cf-navy); font-weight: 800; cursor: pointer; font-family: inherit; line-height: 1 }
.cf-item-cant button:hover { background: var(--cf-teal); color: #fff }
.cf-item-cant span { font-weight: 800; min-width: 18px; text-align: center }
.cf-item-precio { font-weight: 900; color: var(--cf-navy); white-space: nowrap }
.cf-empty { color: var(--cf-muted); text-align: center; padding: 20px 0 }

/* Kit row (cuando hay un kit grande) */
.cf-kit-row { display: flex; justify-content: space-between; align-items: center; gap: 14px; padding: 18px; background: linear-gradient(135deg, rgba(0,196,212,.08), rgba(26,92,184,.08)); border: 1px solid rgba(0,196,212,.25); border-radius: 14px; margin-bottom: 10px }
.cf-kit-tag { font-size: 10px; font-weight: 800; color: var(--cf-teal); letter-spacing: .12em }
.cf-kit-nombre { font-size: 18px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.01em; text-transform: capitalize; margin-top: 2px }
.cf-kit-sub { font-size: 12px; color: var(--cf-muted); margin-top: 4px }
.cf-kit-precio { font-size: 22px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.02em }

/* Totales */
.cf-totales { margin-top: 18px; padding: 14px 16px; background: #f8fafc; border-radius: 12px }
.cf-totales > div:not(.cf-totales-info) { display: flex; justify-content: space-between; align-items: baseline; font-size: 15px }
.cf-totales > div strong { font-size: 22px; font-weight: 900; color: var(--cf-navy) }
.cf-totales-info { font-size: 12px; color: var(--cf-muted); margin-top: 8px }

/* Form */
.cf-field { display: block; margin-bottom: 14px }
.cf-field b { display: block; font-size: 11px; font-weight: 800; color: var(--cf-navy); letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
.cf-req { color: #dc2626 }
.cf-card input, .cf-card select, .cf-card textarea { width: 100%; box-sizing: border-box; padding: 11px 13px; font-size: 14px; border: 1.5px solid var(--cf-line); border-radius: 10px; background: #fff; color: var(--cf-navy); font-family: inherit; transition: border-color .15s, box-shadow .15s }
.cf-card input:focus, .cf-card select:focus, .cf-card textarea:focus { outline: none; border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.cf-row-2-3 { display: grid; grid-template-columns: 2fr 1fr; gap: 12px }
/* Fix 7-jul-2026 (Nico): grid 3 cols para Calle | Número | Apto */
.cf-row-calle-nro { display: grid; grid-template-columns: 3fr 1fr 1fr; gap: 12px }
@media (max-width: 520px) {
  .cf-row-2, .cf-row-2-3 { grid-template-columns: 1fr }
  .cf-row-calle-nro { grid-template-columns: 1fr 1fr; }
  .cf-row-calle-nro > *:first-child { grid-column: 1 / -1 }
}

/* Info */
.cf-info { background: #f0fcfd; border: 1px solid #b3eef3; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: var(--cf-navy); margin-bottom: 12px }
.cf-err { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #991b1b; margin-top: 12px }

/* Suscriptor: dirección "de siempre" (paso 2) */
.cf-direccion-siempre {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 16px 18px; border-radius: 14px;
  background: linear-gradient(135deg, rgba(0,196,212,.10), rgba(26,92,184,.08));
  border: 1.5px solid rgba(0,196,212,.30);
  margin-bottom: 12px;
}
.cf-direccion-siempre-icon { font-size: 26px; line-height: 1; flex-shrink: 0 }
.cf-direccion-siempre-body { flex: 1 }
.cf-direccion-siempre-tag {
  font-size: 10px; font-weight: 800; color: var(--cf-blue);
  letter-spacing: .08em; text-transform: uppercase; margin-bottom: 4px;
}
.cf-direccion-siempre-linea {
  font-size: 16px; font-weight: 800; color: var(--cf-navy);
  letter-spacing: -.01em; line-height: 1.3;
}
.cf-direccion-siempre-barrio {
  font-size: 13px; color: var(--cf-muted); margin-top: 4px;
}
.cf-link-cambiar {
  display: inline-block; margin-bottom: 8px;
  background: transparent; border: 0;
  color: var(--cf-blue); font-weight: 700; font-size: 13px;
  text-decoration: underline; cursor: pointer;
  font-family: inherit; padding: 4px 0;
}
.cf-aviso-otra-direccion {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 10px 14px; margin-bottom: 14px;
  background: #fefce8; border: 1px solid #fcd34d;
  border-radius: 10px; font-size: 12px; color: #78350f;
}
.cf-aviso-otra-direccion button {
  background: transparent; border: 0; color: var(--cf-navy);
  font-weight: 800; font-size: 12px; cursor: pointer;
  font-family: inherit; text-decoration: underline; white-space: nowrap;
}

/* Warning barrio detectado vs elegido (paso 2) */
.cf-warn-barrio {
  display: flex; gap: 12px; align-items: flex-start;
  margin-top: 14px; padding: 14px 16px;
  border-radius: 12px; font-size: 13px;
}
.cf-warn-barrio.is-warn {
  background: #fef3c7; border: 1.5px solid #fcd34d; color: #78350f;
}
.cf-warn-barrio.is-bloqueo {
  background: #fef2f2; border: 1.5px solid #fca5a5; color: #7f1d1d;
}
.cf-warn-barrio-icon { font-size: 24px; line-height: 1; flex-shrink: 0 }
.cf-warn-barrio-body { flex: 1 }
.cf-warn-barrio-body strong { display: block; font-size: 14px; margin-bottom: 6px }
.cf-warn-barrio-body p { margin: 0 0 10px; line-height: 1.45 }
.cf-warn-cta {
  display: inline-block;
  padding: 10px 18px; border-radius: 999px;
  background: var(--cf-navy); color: #fff !important; text-decoration: none;
  border: 0; font-weight: 800; font-size: 13px; cursor: pointer;
  font-family: inherit; margin-right: 8px;
}
.cf-warn-cta:hover { background: var(--cf-blue) }
.cf-warn-link {
  display: block; margin-top: 10px;
  background: transparent; border: 0;
  color: inherit; opacity: .65;
  font-size: 11px; font-weight: 600;
  text-decoration: underline; cursor: pointer;
  font-family: inherit; padding: 4px 0;
}

.cf-confirm-pin { display: flex; align-items: center; gap: 10px; margin-top: 12px; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer }
.cf-confirm-pin.ok   { border: 1.5px solid #22c55e; background: #f0fdf4; color: #166534 }
.cf-confirm-pin.pend { border: 1.5px solid #f59e0b; background: #fefce8; color: #854d0e }
.cf-confirm-pin input { width: 16px !important; height: 16px; margin: 0 }

.cf-btn-secondary { padding: 10px 18px; font-size: 13px; font-weight: 700; background: #fff; color: var(--cf-navy); border: 1.5px solid var(--cf-navy); border-radius: 999px; cursor: pointer; font-family: inherit; transition: background .15s, color .15s }
.cf-btn-secondary:hover:not(:disabled) { background: var(--cf-navy); color: #fff }
.cf-btn-secondary:disabled { opacity: .5; cursor: not-allowed }

/* Selector único de día (paso 3) — dropdown custom */
.cf-dd { position: relative; width: 100% }
.cf-dd-trigger {
  width: 100%; box-sizing: border-box;
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 12px 14px; font-size: 14px; font-family: inherit;
  border: 1.5px solid var(--cf-line); border-radius: 10px;
  background: #fff; color: var(--cf-navy); cursor: pointer;
  text-align: left; transition: border-color .15s, box-shadow .15s;
}
.cf-dd-trigger:hover { border-color: var(--cf-teal) }
.cf-dd-trigger.is-open { border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-dd-trigger-text { flex: 1; font-weight: 700; text-transform: capitalize; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.cf-dd-placeholder { color: var(--cf-muted); font-weight: 500; text-transform: none }
.cf-dd-caret { font-size: 10px; color: var(--cf-muted); flex-shrink: 0 }

.cf-dd-panel {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 50;
  background: #fff; border: 1.5px solid var(--cf-line); border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15,31,61,.12);
  max-height: 280px; overflow-y: auto; padding: 4px;
}
.cf-dd-grupo {
  font-size: 10px; font-weight: 800; color: var(--cf-muted);
  letter-spacing: .06em; text-transform: uppercase;
  padding: 8px 10px 4px;
}
.cf-dd-opt {
  width: 100%; box-sizing: border-box;
  display: flex; justify-content: space-between; align-items: center;
  gap: 10px; padding: 9px 10px; margin: 1px 0;
  background: transparent; border: 0; border-radius: 8px;
  font-family: inherit; font-size: 13px; color: var(--cf-navy);
  cursor: pointer; text-align: left;
}
.cf-dd-opt:hover { background: #f0fcfd }
.cf-dd-opt.is-sel { background: rgba(0,196,212,.12); font-weight: 800 }
.cf-dd-opt.is-kit { background: linear-gradient(135deg, rgba(245,158,11,.08), rgba(245,158,11,.16)); border: 1.5px solid rgba(245,158,11,.45); margin: 4px 0 }
.cf-dd-opt.is-kit:hover { background: linear-gradient(135deg, rgba(245,158,11,.14), rgba(245,158,11,.22)) }
.cf-dd-opt-dia { text-transform: capitalize; flex: 1 }
.cf-dd-kit-badge { display: inline-block; background: linear-gradient(135deg, #F59E0B, #D97706); color: #fff; font-size: 9.5px; font-weight: 800; padding: 2px 8px; border-radius: 999px; letter-spacing: .05em; margin-right: 8px; vertical-align: middle; text-transform: uppercase }
.cf-dd-opt-precio { font-weight: 800; font-size: 12px; color: var(--cf-navy); white-space: nowrap }
.cf-dd-opt-precio.is-free { color: #15803d }

/* Banner sugerencia "Sumar al kit del mes" (suscriptor con kit próximo elegible) */
.cf-sugerencia-kit {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px; border-radius: 12px;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #FFF7ED, #FEF3C7);
  border: 1.5px solid rgba(245,158,11,.4);
  flex-wrap: wrap;
}
.cf-sug-icon { font-size: 28px; flex-shrink: 0 }
.cf-sug-body { flex: 1; min-width: 200px; color: #78350f; line-height: 1.45 }
.cf-sug-body strong { display: block; font-size: 14px; color: #92400e; margin-bottom: 4px }
.cf-sug-body p { margin: 0; font-size: 12.5px }
.cf-sug-btn {
  background: linear-gradient(135deg, #F59E0B, #D97706); color: #fff;
  border: 0; border-radius: 10px; padding: 10px 16px;
  font-family: inherit; font-size: 13px; font-weight: 800;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 2px 6px rgba(245,158,11,.35);
  transition: transform .12s, box-shadow .12s;
}
.cf-sug-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(245,158,11,.5) }

/* Resumen del día elegido (paso 3) */
.cf-fecha-resumen {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 14px 16px; border-radius: 12px;
  margin-top: -4px; margin-bottom: 14px;
  border: 1.5px solid var(--cf-line);
}
.cf-fecha-resumen.is-en-ruta { background: #f0fcfd; border-color: rgba(0,196,212,.4) }
.cf-fecha-resumen.is-fuera   { background: #fefce8; border-color: rgba(245,158,11,.4) }
.cf-fecha-resumen-tag { font-size: 11px; font-weight: 800; letter-spacing: .04em; color: var(--cf-muted); text-transform: uppercase }
.cf-fecha-resumen-dia { font-size: 16px; font-weight: 800; color: var(--cf-navy); text-transform: capitalize; margin-top: 2px }
.cf-fecha-resumen-precio { white-space: nowrap; font-size: 14px; font-weight: 800; color: var(--cf-navy) }
.cf-fecha-resumen-precio .cf-monto-ok { color: #15803d }
.cf-fecha-resumen-kit-match { margin-top: 8px; padding: 6px 10px; background: rgba(245, 158, 11, .12); border: 1px solid rgba(245, 158, 11, .35); border-radius: 8px; font-size: 12.5px; color: #92400e; line-height: 1.4 }
.cf-fecha-resumen-kit-match strong { color: #78350f }
.cf-banner-corte { margin-top: 6px; font-size: 12px; color: var(--cf-muted); line-height: 1.45 }

/* Banner suscriptor / no-suscriptor (paso 3) */
.cf-banner-susc, .cf-banner-info {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px; border-radius: 12px;
  margin-bottom: 18px; font-size: 13px; line-height: 1.5;
}
.cf-banner-susc { background: linear-gradient(135deg, rgba(245,158,11,.12), rgba(0,196,212,.10)); border: 1px solid rgba(245,158,11,.3); color: var(--cf-navy) }
.cf-banner-info { background: #f0fcfd; border: 1px solid rgba(0,196,212,.25); color: var(--cf-navy) }
.cf-banner-icon { font-size: 22px; line-height: 1; flex-shrink: 0 }
/* Solo el strong DIRECTO de primer nivel rompe línea (el título del banner).
   Los strong inline (Pocitos, Lunes y Jueves, etc.) deben mantenerse inline. */
.cf-banner-susc > div > strong:first-child,
.cf-banner-info > div > strong:first-child { display: block; margin-bottom: 4px; font-size: 14px }

/* Fechas (paso 3) */
.cf-fecha-grupo { font-size: 13px; font-weight: 800; color: var(--cf-navy); margin: 14px 0 4px; letter-spacing: .02em }
.cf-fecha-grupo-sub { font-size: 12px; color: var(--cf-muted); margin: 0 0 10px; line-height: 1.45 }
.cf-fechas { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px }
.cf-fecha { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 16px; background: #fff; border: 1.5px solid var(--cf-line); border-radius: 12px; cursor: pointer; font-family: inherit; text-align: left; transition: border-color .15s, background .15s }
.cf-fecha:hover { border-color: var(--cf-teal); background: #f8fafc }
.cf-fecha.is-elegida { border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15); background: #f0fcfd }
.cf-fecha.is-fuera { border-style: dashed; background: #fefdf8 }
.cf-fecha.is-fuera:hover { background: #fef3c7 }
.cf-fecha.is-fuera.is-elegida { border-color: #f59e0b; background: #fef3c7; box-shadow: 0 0 0 3px rgba(245,158,11,.18) }
.cf-fecha-dia { font-size: 15px; font-weight: 800; color: var(--cf-navy); text-transform: capitalize }
.cf-fecha-sub { font-size: 12px; color: var(--cf-muted); margin-top: 2px }
.cf-fecha-precio { white-space: nowrap; font-size: 13px; font-weight: 800 }
.cf-monto-ok   { color: #15803d }
.cf-monto-mid  { color: var(--cf-blue) }
.cf-monto-warn { color: #b45309 }

/* Resumen paso 4 */
.cf-resumen { background: #f8fafc; border-radius: 12px; padding: 14px 18px; margin-bottom: 4px }
.cf-row { display: flex; justify-content: space-between; align-items: baseline; padding: 8px 0; font-size: 14px }
.cf-row + .cf-row { border-top: 1px dashed var(--cf-line) }
.cf-row span:first-child { color: var(--cf-muted); font-weight: 600 }
.cf-row-val { color: var(--cf-navy); text-align: right }
.cf-row.is-big { padding-top: 14px; margin-top: 8px; border-top: 2px solid var(--cf-navy) }
.cf-row.is-big span:first-child { font-size: 13px; color: var(--cf-navy); font-weight: 800; text-transform: uppercase; letter-spacing: .06em }
.cf-row.is-big .cf-row-val { font-size: 22px; font-weight: 900; color: var(--cf-navy) }

/* Pagos */
.cf-pagos { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px }
.cf-pago { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1.5px solid var(--cf-line); border-radius: 12px; cursor: pointer; transition: border-color .15s, background .15s; background: #fff }
.cf-pago:hover { border-color: var(--cf-teal) }
.cf-pago.is-elegido { border-color: var(--cf-teal); background: #f0fcfd; box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-pago.is-soon { opacity: .55; cursor: not-allowed }
.cf-pago.is-soon:hover { border-color: var(--cf-line) }
.cf-pago input { width: 18px !important; height: 18px; margin: 0; flex-shrink: 0 }
.cf-pago > div { display: flex; flex-direction: column; gap: 2px; flex: 1 }
.cf-pago strong { font-size: 14px; color: var(--cf-navy); font-weight: 800 }
.cf-pago span { font-size: 12px; color: var(--cf-muted) }
.cf-soon { display: inline-block; font-style: normal; font-size: 10px; font-weight: 800; color: var(--cf-blue); text-transform: uppercase; letter-spacing: .08em; margin-top: 4px }

/* Nav */
.cf-nav { display: flex; justify-content: space-between; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--cf-line) }
.cf-btn-back { padding: 12px 20px; background: transparent; border: 0; color: var(--cf-muted); font-weight: 700; font-size: 14px; cursor: pointer; border-radius: 999px; font-family: inherit }
.cf-btn-back:hover { background: #f1f5f9; color: var(--cf-navy) }
.cf-btn-next { padding: 14px 22px; font-size: 14px; font-weight: 800; background: linear-gradient(135deg, var(--cf-blue), var(--cf-teal)); color: #fff; border: 0; border-radius: 999px; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.25); transition: transform .12s, box-shadow .12s }
.cf-btn-next:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,196,212,.35) }
.cf-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }

.cf-link { background: none; border: 0; color: var(--cf-blue); cursor: pointer; font-weight: 700; padding: 0; font-family: inherit }

/* Aviso de privacidad (footer del checkout, los 4 pasos) */
.cf-privacy {
  margin-top: 18px; padding: 12px 16px;
  font-size: 11px; line-height: 1.5;
  color: var(--cf-muted); text-align: center;
  background: rgba(248,250,252,.6);
  border-radius: 10px;
  max-width: 540px; margin-left: auto; margin-right: auto;
}
`
